import { createSlice, isPending, isRejected } from '@reduxjs/toolkit';

import { getTutors, getTutor, loadMoreTutors } from './api';
import { IUser } from '../../UserSlice';
import { RootState } from '../..';

interface ITutorRate {
  amount: number;
  currency: string;
}

export interface ITutorBadge {
  text: string;
  color: string;
}

export interface ITutor extends IUser {
  rate?: ITutorRate;
  badge?: ITutorBadge;
  tag?: string;
}

export interface ITutorsState {
  status: 'idle' | 'loading' | 'rejected';
  tutorData: ITutor | undefined;
  tutorsList?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ITutor>;
    studentCurrency: string;
    maxRateAmount?: number;
  };
}

const initialState: ITutorsState = {
  status: 'idle',
  tutorData: undefined,
  tutorsList: undefined,
};

export const TutorsSlice = createSlice({
  name: 'tutors',
  initialState,
  reducers: {
    resetTutor: (state) => {
      state.tutorData = undefined;
    },
    resetTutors: (state) => {
      state.tutorsList = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET TUTOR BY ID
      .addCase(getTutor.fulfilled, (state, action) => {
        state.status = 'idle';
        state.tutorData = action.payload;
      })
      // GET TUTORS
      .addCase(getTutors.fulfilled, (state, action) => {
        state.status = 'idle';
        const {
          limit,
          offset,
          totalDocs = 0,
          docs = [],
          studentCurrency,
          maxRateAmount,
        } = action.payload;

        // Check if price filter is active (minRate or maxRate)
        // Price filter is considered active if either minRate or maxRate is explicitly set in the API request
        const hasPriceFilter =
          (action.meta.arg?.minRate !== undefined &&
            action.meta.arg.minRate !== null) ||
          (action.meta.arg?.maxRate !== undefined &&
            action.meta.arg.maxRate !== null);

        // Sort tutors by price from low to high only if price filter is active
        const sortedDocs = hasPriceFilter
          ? [...docs].sort((a, b) => {
              const rateA = a.rate?.amount || 0;
              const rateB = b.rate?.amount || 0;
              return rateA - rateB;
            })
          : docs;

        state.tutorsList = {
          limit,
          offset,
          totalDocs,
          list: sortedDocs,
          studentCurrency,
          maxRateAmount,
        };
      })
      // LOAD MORE TUTORS
      .addCase(loadMoreTutors.fulfilled, (state, action) => {
        state.status = 'idle';
        const { docs = [] } = action.payload;

        if (state.tutorsList?.list && docs.length) {
          // Check if price filter is active (minRate or maxRate)
          // Price filter is considered active if either minRate or maxRate is explicitly set in the API request
          const hasPriceFilter =
            (action.meta.arg?.minRate !== undefined &&
              action.meta.arg.minRate !== null) ||
            (action.meta.arg?.maxRate !== undefined &&
              action.meta.arg.maxRate !== null);

          const combinedList = [...state.tutorsList.list, ...docs];

          // Sort by price from low to high only if price filter is active
          if (hasPriceFilter) {
            combinedList.sort((a, b) => {
              const rateA = a.rate?.amount || 0;
              const rateB = b.rate?.amount || 0;
              return rateA - rateB;
            });
          }

          state.tutorsList = {
            ...state.tutorsList,
            list: combinedList,
            maxRateAmount: state.tutorsList.maxRateAmount,
          };
        }
      });

    const commonActions = [getTutor, getTutors, loadMoreTutors] as const;

    builder
      // MATCHER
      .addMatcher(isPending(...commonActions), (state) => {
        state.status = 'loading';
      })
      .addMatcher(isRejected(...commonActions), (state) => {
        state.status = 'rejected';
      });
  },
});

export const getTutorsState = (state: RootState): RootState['tutors'] =>
  state.tutors;

export const { resetTutor, resetTutors } = TutorsSlice.actions;

export default TutorsSlice.reducer;
